import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.min.css';

export default function SupplierDebts() {
  const queryClient = useQueryClient();
  
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date(),
    amount: '',
    bank_account_id: '',
    notes: '',
  });

  // Merr llogaritë bankare
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data } = await api.get('/bank-accounts');
        setBankAccounts(data.data || data || []);
      } catch (error) {
        console.error('Gabim duke marrë llogaritë:', error);
      }
    };
    fetchAccounts();
  }, []);

  // Merr furnitorët me detyrime
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-debts'],
    queryFn: async () => {
      const { data } = await api.get('/reports/supplier-debts');
      return data;
    },
  });

  const suppliers = data?.suppliers || [];
  const totalDetyrime = suppliers.filter(s => s.balance > 0).reduce((sum, s) => sum + parseFloat(s.balance), 0);
  const totalPlus = suppliers.filter(s => s.balance < 0).reduce((sum, s) => sum + Math.abs(parseFloat(s.balance)), 0);

  // Ndaj llogaritë në Cash dhe Bankë
  const cashAccounts = bankAccounts.filter(a => a.is_cash === true || a.is_cash === 1);
  const bankOnlyAccounts = bankAccounts.filter(a => a.is_cash === false || a.is_cash === 0);

  const openPaymentModal = (supplier) => {
    setSelectedSupplier(supplier);
    setPaymentForm({
      payment_date: new Date(),
      amount: supplier.balance > 0 ? supplier.balance.toString() : '',
      bank_account_id: '',
      notes: '',
    });
    setPaymentModal(true);
  };

  const selectAccount = (accountId) => {
    setPaymentForm({ ...paymentForm, bank_account_id: accountId.toString() });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Fut shumën e pagesës!');
      return;
    }
    if (!paymentForm.bank_account_id) {
      toast.error('Zgjidh nga cila llogari do paguhet!');
      return;
    }

    setLoading(true);
    try {
      await api.post('/payments', {
        payment_date: paymentForm.payment_date.toISOString().split('T')[0],
        type: 'supplier',
        supplier_id: selectedSupplier.id,
        bank_account_id: paymentForm.bank_account_id,
        amount: paymentForm.amount,
        notes: paymentForm.notes || `Pagesë për ${selectedSupplier.name}`,
      });
      
      toast.success('Pagesa u regjistrua me sukses!');
      setPaymentModal(false);
      queryClient.invalidateQueries(['supplier-debts']);
      queryClient.invalidateQueries(['bank-accounts']);
      queryClient.invalidateQueries(['dashboard']);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim!');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-3xl">🏢</span>
          Detyrimet ndaj Furnitorëve
        </h1>
      </div>

      {/* Kartat përmbledhëse */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
          <p className="text-sm text-gray-500 mb-1">Na Detyrohen</p>
          <p className="text-3xl font-bold text-orange-600">€{totalDetyrime.toFixed(2)}</p>
          <p className="text-sm text-gray-400 mt-1">{suppliers.filter(s => s.balance > 0).length} furnitorë</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <p className="text-sm text-gray-500 mb-1">Kemi Paguar Më Shumë</p>
          <p className="text-3xl font-bold text-green-600">€{totalPlus.toFixed(2)}</p>
          <p className="text-sm text-gray-400 mt-1">{suppliers.filter(s => s.balance < 0).length} furnitorë në plus</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <p className="text-sm text-gray-500 mb-1">Bilanci Neto</p>
          <p className={`text-3xl font-bold ${totalDetyrime - totalPlus > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            €{(totalDetyrime - totalPlus).toFixed(2)}
          </p>
          <p className="text-sm text-gray-400 mt-1">{totalDetyrime - totalPlus > 0 ? 'Për tu paguar' : 'Në plus'}</p>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-4 px-6 font-semibold text-gray-600">Kodi</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-600">Emri Furnitorit</th>
              <th className="text-right py-4 px-6 font-semibold text-gray-600">Shuma</th>
              <th className="text-center py-4 px-6 font-semibold text-gray-600">Statusi</th>
              <th className="text-center py-4 px-6 font-semibold text-gray-600">Veprim</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-12 text-gray-500">
                  <span className="text-4xl block mb-2">✅</span>
                  Nuk ka detyrime ndaj furnitorëve
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => (
                <tr 
                  key={supplier.id} 
                  className={`border-b transition-colors ${
                    supplier.balance > 0 ? 'bg-orange-50 hover:bg-orange-100' : 'bg-green-50 hover:bg-green-100'
                  }`}
                >
                  <td className="py-4 px-6 font-medium">{supplier.code}</td>
                  <td className="py-4 px-6">{supplier.name}</td>
                  <td className="py-4 px-6 text-right">
                    {supplier.balance > 0 ? (
                      <span className="text-orange-600 font-bold text-lg">€{parseFloat(supplier.balance).toFixed(2)}</span>
                    ) : (
                      <span className="text-green-600 font-bold text-lg">+€{Math.abs(parseFloat(supplier.balance)).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-center">
                    {supplier.balance > 0 ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                        🟠 Për Pagesë
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        🟢 Në Plus
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-center">
                    {supplier.balance > 0 ? (
                      <button
                        onClick={() => openPaymentModal(supplier)}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center gap-2 mx-auto"
                      >
                        💳 Paguaj
                      </button>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Pagesë */}
      {paymentModal && selectedSupplier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-orange-600 text-white p-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                💳 Paguaj Furnitorin
              </h2>
              <p className="text-orange-100 mt-1">
                Furnitori: <strong>{selectedSupplier.name}</strong> • Detyrim: <strong>€{parseFloat(selectedSupplier.balance).toFixed(2)}</strong>
              </p>
            </div>
            
            <form onSubmit={handlePayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">📅 Data e Pagesës</label>
                <DatePicker
                  selected={paymentForm.payment_date}
                  onChange={(date) => setPaymentForm({ ...paymentForm, payment_date: date })}
                  dateFormat="dd/MM/yyyy"
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">💶 Shuma</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg pl-8 pr-4 py-3 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">💳 Nga cila llogari?</label>
                
                <div className="space-y-4">
                  {/* CASH Section */}
                  <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <p className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                      💵 CASH (Arka)
                    </p>
                    {cashAccounts.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">Nuk keni arka të regjistruara</p>
                    ) : (
                      <div className="space-y-2">
                        {cashAccounts.map(acc => (
                          <div 
                            key={acc.id} 
                            onClick={() => selectAccount(acc.id)}
                            className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              paymentForm.bank_account_id === acc.id.toString()
                                ? 'border-green-500 bg-green-100 shadow-md' 
                                : 'border-gray-200 bg-white hover:border-green-300'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              paymentForm.bank_account_id === acc.id.toString()
                                ? 'border-green-500 bg-green-500' 
                                : 'border-gray-300'
                            }`}>
                              {paymentForm.bank_account_id === acc.id.toString() && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800">{acc.name}</p>
                              <p className="text-sm text-gray-500">
                                {acc.currency} • Balanca: {acc.currency === 'EUR' ? '€' : 'L'}{parseFloat(acc.balance || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* BANK Section */}
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <p className="text-sm font-bold text-blue-700 mb-3 flex items-center gap-2">
                      🏦 BANKË
                    </p>
                    {bankOnlyAccounts.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">Nuk keni llogari bankare të regjistruara</p>
                    ) : (
                      <div className="space-y-2">
                        {bankOnlyAccounts.map(acc => (
                          <div 
                            key={acc.id} 
                            onClick={() => selectAccount(acc.id)}
                            className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              paymentForm.bank_account_id === acc.id.toString()
                                ? 'border-blue-500 bg-blue-100 shadow-md' 
                                : 'border-gray-200 bg-white hover:border-blue-300'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                              paymentForm.bank_account_id === acc.id.toString()
                                ? 'border-blue-500 bg-blue-500' 
                                : 'border-gray-300'
                            }`}>
                              {paymentForm.bank_account_id === acc.id.toString() && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-800">{acc.name}</p>
                              <p className="text-sm text-gray-500">
                                {acc.currency} • Balanca: {acc.currency === 'EUR' ? '€' : 'L'}{parseFloat(acc.balance || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Nëse nuk ka asnjë llogari */}
                  {cashAccounts.length === 0 && bankOnlyAccounts.length === 0 && (
                    <div className="text-center py-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-yellow-700">⚠️ Nuk keni llogari bankare! Shkoni te Konfigurime → Llogaritë Bankare për të shtuar.</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">📝 Shënim (opsional)</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                  rows="2"
                  placeholder="Shënim për pagesën..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setPaymentModal(false)} 
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Anulo
                </button>
                <button 
                  type="submit" 
                  disabled={loading || !paymentForm.bank_account_id} 
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    '💾'
                  )} 
                  Ruaj Pagesën
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}